import axios from 'axios';
import { ElMessage } from 'element-plus';
// 防止循环引用，我们需要使用import()动态导入
// import authState from './authState';
import eventBus, { EventTypes } from './eventBus';

// 创建axios实例
const request = axios.create({
  baseURL: '', // 基础URL，将与API请求的路径组合
  timeout: 30000, // 超时时间，单位毫秒
});

// 判断是否正在刷新令牌的标志
let isRefreshing = false;
// 存储等待刷新令牌的请求队列
let requestsQueue = [];

// 执行队列中的请求
const processQueue = (error = null) => {
  requestsQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      // 获取最新的令牌
      const token = localStorage.getItem('token');
      if(token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      resolve(request(config)); // 重新发送请求
    }
  });
  // 清空队列
  requestsQueue = [];
};

// 获取authState的函数，避免循环引用
const getAuthState = async () => {
  // 防止循环引用，动态导入
  const authStateModule = await import('./authState');
  return authStateModule.default;
};

// 请求拦截器
request.interceptors.request.use(
  async config => {
    // 如果是刷新令牌的请求，直接使用刷新令牌
    if (config.url && config.url.includes('/v1/auth/refresh')) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        config.headers['Authorization'] = `Bearer ${refreshToken}`;
      }
      return config;
    }
    
    // 对于其他请求，检查访问令牌是否有效
    const token = localStorage.getItem('token');
    if (token) {
      // 验证令牌状态并在需要时刷新
      try {
        const authState = await getAuthState();
        if (authState.token && authState.token.value) {
          const isValid = await authState.validateToken();
          if (!isValid) {
            console.warn('令牌无效，请求可能会失败');
          }
        }
      } catch (error) {
        console.error('验证令牌时出错:', error);
      }
      
      // 无论刷新是否成功，都使用当前最新的令牌（可能是新刷新的，也可能是原来的）
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers['Authorization'] = `Bearer ${currentToken}`;
      }
    }
    
    return config;
  },
  error => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  response => {
    // 如果响应成功，直接返回数据
    return response;
  },
  async error => {
    // 如果响应失败，处理错误
    console.error('响应错误:', error);
    
    // 处理错误消息
    let errorMessage = '请求失败';
    
    if (error.response) {
      // 服务器返回了错误状态码
      const { status, data, config } = error.response;
      
      // 动态获取authState
      const authState = await getAuthState();
      
      // 如果是令牌过期错误且存在刷新令牌，尝试刷新令牌后重试请求
      if (status === 401 && authState.refreshToken && authState.refreshToken.value && !config.url.includes('/v1/auth/refresh')) {
        // 如果当前没有刷新过程
        if (!isRefreshing) {
          isRefreshing = true;
          
          try {
            const refreshSuccess = await authState.refreshAccessToken();
            if (refreshSuccess) {
              // 刷新成功，处理队列中的请求
              processQueue();
              // 重试当前请求
              const token = localStorage.getItem('token');
              if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
              }
              return request(config);
            } else {
              // 刷新失败，触发令牌过期事件并执行登出
              eventBus.emit(EventTypes.AUTH_TOKEN_EXPIRED, {
                code: 'TOKEN_REFRESH_FAILED',
                message: '您的登录已过期，请重新登录',
                originalRequest: config
              });
              authState.logout(); // 执行登出操作
              processQueue(error);
            }
          } catch (refreshError) {
            // 处理刷新过程中的错误
            console.error('刷新令牌过程中出错:', refreshError);
            eventBus.emit(EventTypes.AUTH_TOKEN_EXPIRED, {
              code: 'TOKEN_REFRESH_ERROR',
              message: '刷新登录状态失败，请重新登录',
              originalRequest: config
            });
            authState.logout(); // 执行登出操作
            processQueue(error);
          } finally {
            isRefreshing = false;
          }
          
          // 返回一个新的Promise阻止原始错误继续传播
          return new Promise((resolve, reject) => {
            requestsQueue.push({ resolve, reject, config });
          });
        } else {
          // 如果已经有刷新过程，将当前请求加入队列
          return new Promise((resolve, reject) => {
            requestsQueue.push({ resolve, reject, config });
          });
        }
      } else {
        // 处理其他错误状态码
        switch (status) {
          case 400:
            errorMessage = data.message || '请求参数错误';
            break;
          case 401:
            errorMessage = '未授权，请重新登录';
            // 触发令牌过期事件并执行登出
            eventBus.emit(EventTypes.AUTH_TOKEN_EXPIRED, {
              code: 'TOKEN_EXPIRED',
              message: '您的登录已过期，请重新登录',
              originalRequest: config
            });
            authState.logout(); // 执行登出操作
            break;
          case 403:
            errorMessage = '拒绝访问';
            break;
          case 404:
            errorMessage = '请求的资源不存在';
            break;
          case 500:
            errorMessage = '服务器内部错误';
            break;
          default:
            errorMessage = `请求失败(${status})`;
        }
      }
    } else if (error.request) {
      // 请求已发送但没有收到响应
      errorMessage = '服务器没有响应';
    }
    
    // 显示错误消息
    ElMessage.error(errorMessage);
    
    return Promise.reject(error);
  }
);

export default request; 